import { Loading } from "@/components/common";
import { ChatMessage as ChatMessageType } from "@/types/chat";
import { cn } from "@/utils/cn";
import React, { useEffect, useRef, useState } from "react";
import { ChatHeader } from "./ChatHeader";
import { ChatInput } from "./ChatInput";
import { ChatMessage } from "./ChatMessage";
import { PromptSuggestions } from "./PromptSuggestions";

interface ChatContainerProps {
  sessionId: string;
  messages: ChatMessageType[];
  status: "connected" | "disconnected" | "connecting" | "error";
  loading?: boolean;
  onSendMessage: (message: string) => void;
  onRefresh?: () => void;
  onClear?: () => void;
  className?: string;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
  sessionId,
  messages,
  status,
  loading = false,
  onSendMessage,
  onRefresh,
  onClear,
  className = "",
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Auto-scroll para a última mensagem
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Simular "digitando..." quando status é connecting
  useEffect(() => {
    if (status === "connecting") {
      setIsTyping(true);
      const timer = setTimeout(() => setIsTyping(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleSendMessage = (message: string) => {
    onSendMessage(message);
    setInputValue("");
    setIsTyping(true);
    // Simular resposta do assistente
    setTimeout(() => setIsTyping(false), 1000);
  };

  const handleSelectPrompt = (prompt: string) => {
    setInputValue(prompt);
  };

  return (
    <div
      className={cn(
        "flex flex-col h-full min-h-0 bg-gray-50 rounded-lg overflow-hidden",
        className
      )}
    >
      {/* Header */}
      <ChatHeader
        sessionId={sessionId}
        messageCount={messages.length}
        onRefresh={onRefresh}
        onClear={onClear}
      />

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
              <span className="text-2xl">💬</span>
            </div>
            <h3 className="text-lg font-medium mb-2">Nenhuma mensagem ainda</h3>
            <p className="text-sm">
              Clique no botão "Nova Sessão" para começar uma conversa!
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex items-start space-x-3 p-4">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-gray-600 text-sm">🤖</span>
                </div>
                <div className="bg-gray-100 p-3 rounded-lg rounded-bl-none">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    />
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <Loading type="spinner" text="Carregando mensagens..." />
        </div>
      )}

      {/* Prompt Suggestions */}
      {sessionId !== "sem-sessao" && (
        <PromptSuggestions onSelectPrompt={handleSelectPrompt} />
      )}

      {/* Input Area */}
      <ChatInput
        onSendMessage={handleSendMessage}
        disabled={
          sessionId === "sem-sessao" &&
          (status === "disconnected" || status === "error")
        }
        loading={loading && sessionId === "sem-sessao"}
        placeholder={
          sessionId === "sem-sessao" && status === "disconnected"
            ? "Desconectado do servidor"
            : sessionId === "sem-sessao" && status === "error"
            ? "Erro de conexão"
            : "Digite sua mensagem..."
        }
        initialValue={inputValue}
        onValueChange={setInputValue}
      />
    </div>
  );
};
